import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

const tiers = [
  {
    id: "starter",
    name: "Starter",
    tagline: "For solo operators & small teams.",
    monthly: 29, annual: 290,
    features: [
      "1 product module (Queue / Salon / Clinic)",
      "Up to 5 users",
      "Standard support",
      "Basic analytics",
      "Email integrations",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    tagline: "Growing studios & multi-branch businesses.",
    monthly: 99, annual: 990,
    featured: true,
    features: [
      "All Starter features",
      "Up to 3 product modules",
      "AI Marketplace access (Free models)",
      "Up to 50 users",
      "Priority support",
      "Advanced analytics & exports",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    tagline: "Hospitals, banks, government & global ops.",
    monthly: 299, annual: 2990,
    features: [
      "Unlimited modules & users",
      "Full AI Marketplace (Premium models)",
      "AI Voice Bot included",
      "Dedicated success manager",
      "Custom integrations & SLA",
      "On-premise option",
    ],
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="section" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow mb-4">Pricing</div>
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
            Simple plans. <span className="gold-text italic">Serious value.</span>
          </h2>
          <p className="mt-5 text-white/55">
            Start free, scale as you grow. Annual billing saves you two months.
          </p>
        </div>

        {/* Toggle */}
        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={`text-sm ${!annual ? "text-white" : "text-white/40"}`}>Monthly</span>
          <button
            data-testid="billing-toggle"
            onClick={() => setAnnual((a) => !a)}
            className={`relative h-7 w-12 rounded-full border transition-colors ${annual ? "bg-gold-500/40 border-gold-500/50" : "bg-white/5 border-white/10"}`}
            aria-label="Toggle billing cycle"
          >
            <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-gradient-to-b from-gold-200 to-gold-500 transition-all ${annual ? "left-[22px]" : "left-0.5"}`} />
          </button>
          <span className={`text-sm flex items-center gap-2 ${annual ? "text-white" : "text-white/40"}`}>
            Annual <span className="font-mono text-[10px] text-gold-300 border border-gold-500/30 rounded-full px-2 py-0.5">SAVE 17%</span>
          </span>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {tiers.map((t) => {
            const price = annual ? t.annual : t.monthly;
            const cycle = annual ? "/yr" : "/mo";
            return (
              <div
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className={`relative rounded-2xl p-8 border transition-all ${
                  t.featured
                    ? "bg-gradient-to-b from-gold-500/[0.08] to-transparent border-gold-500/40 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.4)]"
                    : "glass-card border-white/10"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-gold-500 text-ink px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em]">
                    <Sparkles className="h-3 w-3" /> Most Popular
                  </span>
                )}
                <div className="font-serif text-2xl text-white">{t.name}</div>
                <div className="mt-1 text-sm text-white/55">{t.tagline}</div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-serif text-5xl gold-text">${price}</span>
                  <span className="text-white/55 text-sm">{cycle}</span>
                </div>
                <Link
                  to="/register"
                  data-testid={`pricing-cta-${t.id}`}
                  className={`mt-7 w-full inline-flex justify-center pill ${t.featured ? "pill-gold" : "pill-ghost"} text-sm`}
                >
                  Start {t.name}
                </Link>
                <ul className="mt-8 space-y-3">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/75">
                      <Check className="mt-0.5 h-4 w-4 text-gold-400 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs font-mono uppercase tracking-[0.2em] text-white/40">
          Accepts Stripe · Razorpay · PayPal
        </div>
      </div>
    </section>
  );
}
